// Web-only (no Swift counterpart): every tile host the map uses, whether it sends CORS headers, and how the service
// worker treats its tiles. src/sw/sw.js keeps the same list in its `TILE_HOSTS` array (a test keeps the two equal).
//
// CORS was checked on 2026-10-20 with `curl -sI -H 'Origin: https://app.trailbivvy.com' <tile URL>`: every host below
// answered `Access-Control-Allow-Origin` (`*`, or the origin echoed by sgx.geodatenzentrum.de). Terms per host:
// src/map/PROVIDERS.md.

export type ServiceWorkerTilePolicy =
  /** Downloaded tiles (tb-tiles-v1) first, then the ambient LRU cache (tb-tiles-ambient-v1), then the network. */
  | 'cache'
  /** Never stored by the app: the service worker lets the browser fetch it (HTTP cache only). */
  | 'network';

export interface TileHostPolicy {
  host: string;
  /** Sends `Access-Control-Allow-Origin` for the app's origin, so tiles can be read by script and cached. */
  cors: boolean;
  policy: ServiceWorkerTilePolicy;
}

export const TILE_HOSTS: readonly TileHostPolicy[] = [
  { host: 'wmts.geo.admin.ch', cors: true, policy: 'cache' }, // swisstopo / FOEN WMTS
  { host: 'data.geopf.fr', cors: true, policy: 'cache' }, // IGN France Géoplateforme WMTS
  { host: 'mapsneu.wien.gv.at', cors: true, policy: 'cache' }, // basemap.at
  { host: 'sgx.geodatenzentrum.de', cors: true, policy: 'cache' }, // BKG TopPlusOpen
  { host: 'www.ign.es', cors: true, policy: 'cache' }, // IGN España WMTS
  // Kartverket: local storage of its cache-service tiles is not permitted without the rights holders' consent
  // (z12–20 are Geovekst data): browser HTTP cache only.
  { host: 'cache.kartverket.no', cors: true, policy: 'network' },
  { host: 'basemap.nationalmap.gov', cors: true, policy: 'cache' }, // USGS The National Map
  // OpenStreetMap: the OSMF tile usage policy forbids offline use and prefetching: browser HTTP cache only.
  { host: 'tile.openstreetmap.org', cors: true, policy: 'network' },
];

/** Host of a URL or URL template (undefined when it does not parse). */
export function hostOf(urlOrTemplate: string): string | undefined {
  try {
    return new URL(urlOrTemplate.replace(/\{[xyz]\}/g, '0')).host;
  } catch {
    return undefined;
  }
}

export function tileHostPolicy(urlOrTemplate: string): TileHostPolicy | undefined {
  const host = hostOf(urlOrTemplate);
  return host === undefined ? undefined : TILE_HOSTS.find((h) => h.host === host);
}

/** Whether tiles of this URL template can be fetched with CORS (unknown hosts: no). */
export function corsEnabledFor(urlTemplate: string): boolean {
  return tileHostPolicy(urlTemplate)?.cors ?? false;
}
